'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faList,
  faSearch,
  faTrashCanXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useProspectSelection } from 'src/context/prospects';
import { RemoveProvidersDialog } from 'src/modules/Lists/components';
import { TableExportMenu } from '../TableExportMenu';
import { TableColumnMenu } from '../TableColumnMenu';
import { TableAddToListButton } from '../TableAddToListButton';
import { PulseButton } from 'src/components';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { TableMoveToListButton } from '../TableMoveToListButton';

interface EncountersToolbar {
  location?: string;
  onSearch?: (value: string) => void;
  // Export button props
  onExport?: (type: string) => void;
  isExporting?: boolean;
  isExportDisabled?: boolean;
  // Columns button props
  columns?: any[];
  columnVisibilityModel?: any;
  setColumnVisibility?: any;
  // Feature flags
  showExportButton?: boolean;
  showDeleteButton?: boolean;
  showAddToListButton?: boolean;
  showProviderFilterButton?: boolean;
  showMoveToListButton?: boolean;
}

const EncountersToolbar = (params: EncountersToolbar) => {
  const {
    showExportButton = false,
    showDeleteButton = false,
    showAddToListButton = false,
    showProviderFilterButton: _showProviderFilterButton = true,
    showMoveToListButton = false,
    location,
    ...restParams
  } = params;

  const { closeAllButCurrent } = useDrawer();

  const showListUploaderButton = location === LOCATION.LISTS;

  const {
    state: [selectionModel],
  } = useProspectSelection();

  const [search, setSearch] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    restParams?.onSearch?.(e.target.value);
  };

  const handleExport = (type: string) => {
    if (!type) return;
    restParams?.onExport?.(type);
  };

  // Height is arbitrary value to match designs
  return (
    <>
      <div className="flex items-center justify-between w-full h-[72px] bg-white px-4 py-4 border-b border-neutral-300">
        <div className="flex items-center gap-2">
          <div className="w-72 h-9 bg-white rounded-full outline outline-[0.83px] outline-offset-[-0.83px] outline-neutral-300 inline-flex justify-start items-center gap-1.5 px-3.5">
            <Icon icon={faSearch} className="w-3.5 h-3.5 text-neutral-600" />
            <input
              type="text"
              value={search}
              placeholder={
                location === LOCATION.TERRITORY_ANALYTICS
                  ? 'Search Territories'
                  : 'Search List'
              }
              className="flex-1 bg-transparent border-none outline-none text-primary text-xs font-medium font-['Inter'] leading-none placeholder:text-tertiary"
              onChange={handleSearch}
            />
          </div>
          {showDeleteButton && (
            <button
              type="button"
              className={`w-9 h-9 px-3.5 py-3 bg-white rounded-full outline outline-[0.83px] outline-offset-[-0.83px] outline-neutral-300 inline-flex justify-center items-center gap-1.5 ${
                selectionModel.length
                  ? 'hover:bg-neutral-100 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => selectionModel.length && setShowDeleteDialog(true)}
              aria-label="Delete selected items"
              data-testid={`${location}-provider-delete-button`}
              disabled={!selectionModel.length}
            >
              <Icon
                icon={faTrashCanXmark}
                className={`w-3 h-3 ${
                  selectionModel.length
                    ? 'text-neutral-600'
                    : 'text-neutral-400'
                }`}
              />
            </button>
          )}
          {showMoveToListButton && (
            <TableMoveToListButton disabled={!selectionModel.length} />
          )}
          {showAddToListButton && <TableAddToListButton />}
        </div>
        <div className="flex items-center gap-2">
          {showListUploaderButton && (
            <PulseButton
              size="sm"
              variant="outline"
              color="neutral"
              startDecorator={faList}
              onClick={() => {
                closeAllButCurrent('listsUploader');
              }}
              className="w-full max-w-36 h-9 rounded-full"
            >
              List Uploader
            </PulseButton>
          )}
          {showExportButton && (
            <TableExportMenu
              isExporting={restParams?.isExporting}
              table={location}
              onExport={handleExport}
              disabled={restParams?.isExportDisabled}
            />
          )}
          <TableColumnMenu
            table={location}
            columns={restParams?.columns}
            columnVisibilityModel={restParams?.columnVisibilityModel}
            setColumnVisibilityModel={restParams?.setColumnVisibility}
          />
        </div>
      </div>
      <RemoveProvidersDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default EncountersToolbar;
